// Textos legais da contratação pública (/assinar): Termos de Contratação e Política de Privacidade.
//
// Ao alterar qualquer texto em substância, mude LEGAL_VERSION aqui E TERMS_VERSION em
// supabase/functions/asaas-signup/index.ts, para o registro de aceite refletir a versão certa.

export const LEGAL_VERSION = 'v3-2026-10';
export const LEGAL_UPDATED_AT = 'outubro de 2026';

// Identificação da Contratada. Preencha os campos vazios com os dados oficiais; os preenchidos
// aparecem no topo dos documentos.
export const LEGAL_COMPANY = {
    tradeName: 'Evolua Negócios',
    product: 'Orion CRM',
    legalName: '', // Razão social. Ex.: "Evolua Negócios Ltda."
    cnpj: '', // Ex.: "00.000.000/0001-00"
    address: '', // Endereço completo da sede
    contactUrl: 'https://evoluanegocios.com',
    contactEmail: '', // E-mail de atendimento e do encarregado de dados (LGPD)
};

// Valores comerciais citados nos Termos (mantenha igual a supabase/functions/_shared/asaas.ts)
const IMPLEMENTATION_FEE = 'R$ 399,00';
const MONTHLY_FEE = 'R$ 150,00';
const EXTRA_USER_FEE = 'R$ 39,90';

export type LegalBlock = string | { list: string[] };
export interface LegalSection {
    title: string;
    blocks: LegalBlock[];
}
export interface LegalDocument {
    title: string;
    intro: string;
    sections: LegalSection[];
}

const contactLine = LEGAL_COMPANY.contactEmail
    ? `pelo e-mail ${LEGAL_COMPANY.contactEmail} ou pelo site ${LEGAL_COMPANY.contactUrl}`
    : `pelos canais de atendimento indicados em ${LEGAL_COMPANY.contactUrl}`;

export const TERMS: LegalDocument = {
    title: 'Termos de Contratação',
    intro:
        `Estes Termos regulam a contratação e o uso do ${LEGAL_COMPANY.product}, sistema de gestão comercial (CRM) ` +
        `oferecido pela ${LEGAL_COMPANY.tradeName} em parceria com o Método ACM. Ao marcar a caixa de aceite na página de contratação, ` +
        'você declara que leu e concorda com estas condições e com a Política de Privacidade.',
    sections: [
        {
            title: 'Definições',
            blocks: [
                { list: [
                    `Contratada: ${LEGAL_COMPANY.tradeName}, responsável pela oferta e pela operação do ${LEGAL_COMPANY.product}.`,
                    'Contratante: a pessoa física ou jurídica que preenche o cadastro e contrata o serviço.',
                    'Plataforma: o sistema web e aplicativos do Orion CRM, com suas funcionalidades.',
                    'Usuário: cada pessoa com login individual de acesso à conta do Contratante.',
                    'Implantação: pagamento único e opcional pela criação e configuração da conta.',
                    'Mensalidade: valor mensal pelo direito de uso da Plataforma.',
                    'Período Contratado: 12 (doze) meses de Mensalidade, que valem para qualquer forma de pagamento, inclusive a mensal.',
                ] },
            ],
        },
        {
            title: 'Objeto',
            blocks: [
                'A Contratada concede ao Contratante uma licença de uso da Plataforma na modalidade de software como serviço (SaaS), ' +
                'de forma não exclusiva e intransferível, durante a vigência da contratação e mediante o pagamento dos valores previstos.',
                'A Plataforma é entregue como está disponível na data da contratação, podendo receber melhorias e alterações ao longo do tempo.',
            ],
        },
        {
            title: 'Cadastro e acesso',
            blocks: [
                'O Contratante deve informar dados verdadeiros, completos e atualizados. Dados incorretos podem impedir a emissão da cobrança, ' +
                'a criação da conta ou o contato, e são de responsabilidade de quem os informou.',
                'Após a confirmação do pagamento inicial, a conta do Contratante e o acesso do gestor são criados automaticamente. ' +
                'O login é o e-mail informado no cadastro. A senha inicial é provisória, informada na contratação, e recomendamos que seja alterada no primeiro acesso.',
                'O Contratante é responsável por guardar as credenciais, por todos os acessos feitos com elas e pelos Usuários que cadastrar. ' +
                'Cada login é pessoal e não deve ser compartilhado. Suspeita de acesso indevido deve ser comunicada imediatamente à Contratada.',
            ],
        },
        {
            title: 'Planos, Usuários e valores',
            blocks: [
                { list: [
                    `Plano de 12 meses: a contratação é pelo Período Contratado de 12 meses, com Mensalidade de ${MONTHLY_FEE} por mês, em qualquer forma de pagamento.`,
                    `Implantação (opcional): ${IMPLEMENTATION_FEE}, cobrada uma única vez, somente se o Contratante marcar essa opção na contratação.`,
                    'Início da Mensalidade: com Implantação, o primeiro mês corresponde à Implantação e a Mensalidade é cobrada a partir do segundo mês. Sem Implantação, a primeira Mensalidade é cobrada no ato da contratação.',
                    'Pagamento em 12x: no cartão de crédito, o Contratante paga antecipadamente as 12 Mensalidades (e a Implantação, se contratada), parceladas em até 12 vezes, com desconto de 5% sobre o total.',
                    'Usuários incluídos: 2 (um gestor e um vendedor).',
                    `Usuário adicional: ${EXTRA_USER_FEE} por usuário, por mês, contratado dentro da própria Plataforma (Configurações > Assinatura).`,
                ] },
                'Os valores são em reais. Durante o Período Contratado, a Mensalidade contratada não é alterada; depois dele, os valores podem ser alterados conforme a cláusula "Alterações destes Termos e dos preços".',
            ],
        },
        {
            title: 'Cupons e condições promocionais',
            blocks: [
                'Na contratação, o Contratante pode aplicar um cupom, que concede os benefícios exibidos na página antes da confirmação: desconto na Implantação, ' +
                'desconto na Mensalidade e/ou meses sem cobrança de Mensalidade. Os valores e prazos finais são os mostrados no resumo da contratação.',
                { list: [
                    'O cupom é pessoal, de uso único por cliente (CPF/CNPJ ou e-mail) e sujeito a prazo de validade e a limite de usos.',
                    'Os cupons não são cumulativos entre si e não podem ser convertidos em dinheiro.',
                    'O desconto na Mensalidade, quando houver, vale enquanto a contratação estiver ativa e não incide sobre os Usuários adicionais.',
                    'Em contratação com Implantação gratuita, a conta é criada sem pagamento inicial e as Mensalidades seguem as regras destes Termos.',
                    'A Contratada pode encerrar a oferta de um cupom a qualquer momento, sem afetar contratações já concluídas com ele.',
                ] },
                'Se o cupom for aplicado de forma indevida ou fraudulenta, a Contratada poderá cobrar a diferença, suspender o acesso ou cancelar a contratação.',
            ],
        },
        {
            title: 'Usuários adicionais',
            blocks: [
                'O Contratante pode adicionar Usuários pela Plataforma. O acesso adicional é liberado imediatamente e o valor correspondente é somado à Mensalidade, ' +
                'passando a constar a partir da próxima cobrança.',
                'A redução de Usuários adicionais deve ser solicitada à Contratada e produz efeito a partir do ciclo de cobrança seguinte.',
            ],
        },
        {
            title: 'Pagamento',
            blocks: [
                'Os pagamentos são processados pela instituição de pagamento Asaas, por Pix ou cartão de crédito, conforme as opções exibidas na contratação. Não há pagamento por boleto bancário.',
                'Na opção de Pix mensal, o Contratante paga o valor inicial por Pix na contratação e recebe uma cobrança por Pix a cada mês, devendo pagá-la dentro do prazo de vencimento.',
                'Na opção de cartão com cobrança automática, o Contratante autoriza, ao aceitar estes Termos, a cobrança do valor inicial no ato da contratação ' +
                '(a Implantação, se contratada; senão, a primeira Mensalidade) ' +
                `e da Mensalidade (${MONTHLY_FEE}, acrescida de ${EXTRA_USER_FEE} por Usuário adicional contratado) de forma recorrente, todo mês, no mesmo cartão, ` +
                'durante o Período Contratado e, depois dele, até o cancelamento. Para isso, o cartão é registrado de forma segura (tokenização) junto ao Asaas. ' +
                'A Contratada não armazena o número completo do cartão nem o código de segurança.',
                'Na opção de cartão em 12x, o Contratante autoriza a cobrança do valor total do Período Contratado (12 Mensalidades e, se contratada, a Implantação), com desconto de 5%, ' +
                'parcelado em 12 vezes no cartão de crédito. As parcelas e o limite do cartão são administrados pela operadora do cartão. Os Usuários adicionais são cobrados à parte, mensalmente.',
                'Se a cobrança no cartão for recusada (por limite, validade, bloqueio ou outro motivo), o Contratante poderá regularizar pagando a fatura emitida ou atualizando o meio de pagamento pelos canais de atendimento.',
            ],
        },
        {
            title: 'Atraso e suspensão do acesso',
            blocks: [
                'O acesso é mantido por até 5 (cinco) dias após o vencimento da cobrança. Persistindo o não pagamento, o acesso à Plataforma poderá ser suspenso ' +
                'até a regularização, sem prejuízo da cobrança dos valores devidos.',
                'Sobre valores pagos em atraso incidem os encargos previstos na própria cobrança, nos limites da lei. A confirmação do pagamento restabelece o acesso.',
                'A suspensão por inadimplência não extingue a contratação nem as obrigações de pagamento já vencidas.',
            ],
        },
        {
            title: 'Direito de arrependimento',
            blocks: [
                'Quando o Contratante for consumidor, nos termos do Código de Defesa do Consumidor, ele pode desistir da contratação em até 7 (sete) dias corridos ' +
                'contados da data do pagamento, por contratação feita fora de estabelecimento comercial, com a devolução integral dos valores pagos, inclusive da Implantação.',
                `Para exercer o direito, basta solicitar pelos canais de atendimento (${contactLine}). O estorno será feito pelo mesmo meio de pagamento utilizado.`,
            ],
        },
        {
            title: 'Cancelamento',
            blocks: [
                'Durante o Período Contratado de 12 meses, o Contratante pode solicitar o cancelamento pelos canais de atendimento, mas o cancelamento não dispensa o pagamento ' +
                'das Mensalidades dos meses restantes do Período Contratado, que seguem sendo cobradas na forma contratada. O acesso permanece disponível até o fim do período pago. ' +
                'Na contratação paga em 12x, não há devolução dos valores já pagos pelo Período Contratado.',
                'Depois do Período Contratado, o Contratante pode cancelar a qualquer momento, sem multa. A cobrança mensal (inclusive a automática no cartão) é encerrada e o cancelamento ' +
                'produz efeito ao final do período já pago.',
                'Ficam ressalvados o direito de arrependimento e as demais hipóteses previstas em lei, que prevalecem sobre esta cláusula.',
                'Encerrada a contratação, o acesso à Plataforma é interrompido. O tratamento e a eventual exclusão dos dados seguem a Política de Privacidade.',
            ],
        },
        {
            title: 'Uso adequado',
            blocks: [
                'É vedado ao Contratante e aos seus Usuários:',
                { list: [
                    'usar a Plataforma para fins ilícitos, fraudulentos ou que violem direitos de terceiros;',
                    'inserir dados pessoais de terceiros sem base legal para tratá-los;',
                    'enviar mensagens em massa não solicitadas (spam) ou desrespeitar as regras dos serviços integrados, como o WhatsApp;',
                    'tentar acessar áreas, contas ou dados que não lhe pertencem, ou comprometer a segurança e o desempenho da Plataforma;',
                    'copiar, revender, sublicenciar ou fazer engenharia reversa da Plataforma.',
                ] },
                'O descumprimento pode levar à suspensão ou ao encerramento do acesso, sem prejuízo de outras medidas cabíveis.',
            ],
        },
        {
            title: 'Dados do Contratante e proteção de dados',
            blocks: [
                'Os dados que o Contratante e seus Usuários inserem na Plataforma (por exemplo, leads, clientes, conversas e propostas) pertencem ao Contratante. ' +
                'Em relação a esses dados, o Contratante atua como controlador e a Contratada como operadora, tratando-os apenas para prestar o serviço e conforme as instruções do Contratante.',
                'Em relação aos dados do próprio cadastro e da contratação, a Contratada atua como controladora.',
                'O tratamento de dados pessoais é detalhado na Política de Privacidade, que integra estes Termos.',
            ],
        },
        {
            title: 'Propriedade intelectual',
            blocks: [
                'A Plataforma, sua marca, interface, código e conteúdos são de titularidade da Contratada ou de seus licenciantes. ' +
                'Estes Termos não transferem ao Contratante qualquer direito de propriedade, apenas o direito de uso previsto no objeto.',
            ],
        },
        {
            title: 'Disponibilidade e suporte',
            blocks: [
                'A Contratada busca manter a Plataforma disponível, mas não garante funcionamento ininterrupto ou livre de erros. ' +
                'Podem ocorrer manutenções, atualizações e indisponibilidades, inclusive de serviços de terceiros dos quais a Plataforma depende.',
                `O atendimento e o suporte são prestados pelos canais de contato indicados em ${LEGAL_COMPANY.contactUrl}.`,
                'Funcionalidades que dependem de serviços de terceiros ativados pelo Contratante (como WhatsApp, agenda e assinatura eletrônica) ' +
                'estão sujeitas aos termos e à disponibilidade desses serviços.',
            ],
        },
        {
            title: 'Limitação de responsabilidade',
            blocks: [
                'A Contratada responde pelos danos diretos que causar, nos limites da lei. Não responde por lucros cessantes, perda de oportunidades de negócio ou danos indiretos, ' +
                'nem por prejuízos decorrentes de uso indevido da Plataforma, de credenciais compartilhadas ou perdidas, de informações incorretas inseridas pelo Contratante ou de falhas de serviços de terceiros.',
                'Nada nestes Termos exclui direitos que a lei assegure ao consumidor e que não possam ser afastados.',
            ],
        },
        {
            title: 'Alterações destes Termos e dos preços',
            blocks: [
                'A Contratada pode atualizar estes Termos e os valores. As mudanças serão comunicadas com antecedência mínima de 30 (trinta) dias, por e-mail ou pela Plataforma, ' +
                'e não alteram o ciclo já pago.',
                'Se o Contratante não concordar com a mudança, poderá cancelar a contratação antes de ela entrar em vigor. O uso da Plataforma após essa data representa concordância.',
            ],
        },
        {
            title: 'Vigência',
            blocks: [
                'A contratação vigora pelo Período Contratado de 12 meses, a partir da confirmação do pagamento inicial. Terminado o período, ela se renova mensalmente, ao valor da Mensalidade vigente e por Pix, ' +
                'até o cancelamento por qualquer das partes ou a rescisão por descumprimento.',
            ],
        },
        {
            title: 'Disposições gerais e foro',
            blocks: [
                'Estes Termos são regidos pelas leis brasileiras. Se qualquer cláusula for considerada inválida, as demais permanecem em vigor.',
                'Fica eleito o foro do domicílio do Contratante, quando consumidor. Nos demais casos, o foro da comarca da sede da Contratada.',
                'Comunicações podem ser feitas ao e-mail informado no cadastro e pela Plataforma.',
            ],
        },
    ],
};

export const PRIVACY: LegalDocument = {
    title: 'Política de Privacidade',
    intro:
        `Esta Política explica como a ${LEGAL_COMPANY.tradeName} trata dados pessoais na contratação e no uso do ${LEGAL_COMPANY.product}, ` +
        'em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018, LGPD).',
    sections: [
        {
            title: 'Quem é responsável pelos dados',
            blocks: [
                `Quanto aos dados do cadastro e da contratação (descritos abaixo), a ${LEGAL_COMPANY.tradeName} é a controladora.`,
                'Quanto aos dados que o cliente insere na Plataforma (leads, clientes, conversas, propostas etc.), o cliente é o controlador e a ' +
                `${LEGAL_COMPANY.tradeName} atua como operadora, tratando esses dados somente para prestar o serviço, conforme as instruções do cliente.`,
            ],
        },
        {
            title: 'Dados que coletamos',
            blocks: [
                'No cadastro e na contratação:',
                { list: [
                    'nome do gestor e nome da empresa;',
                    'e-mail (usado como login) e telefone/WhatsApp;',
                    'CPF ou CNPJ;',
                    'data, hora, endereço IP e versão dos textos aceitos (registro do seu consentimento);',
                    'situação da contratação e histórico de cobranças e pagamentos.',
                ] },
                'No pagamento com cartão com cobrança automática, você informa os dados do cartão, o CEP e o número do endereço do titular. ' +
                'Esses dados são enviados ao Asaas para processar a cobrança e não ficam guardados em nosso sistema (veja a seção sobre pagamentos).',
                'No uso da Plataforma: dados de login, registros de acesso (como data, hora e IP) e informações técnicas necessárias à segurança e ao funcionamento.',
            ],
        },
        {
            title: 'Para que usamos os dados e em qual base legal',
            blocks: [
                { list: [
                    'Criar a conta, entregar a Plataforma e cobrar pelo serviço: execução do contrato (LGPD, art. 7º, V).',
                    'Emitir cobranças, controlar pagamentos e cumprir obrigações fiscais e legais: cumprimento de obrigação legal ou regulatória (art. 7º, II).',
                    'Registrar o aceite dos Termos e da cobrança recorrente, e exercer direitos em eventual disputa: exercício regular de direitos (art. 7º, VI) e execução do contrato.',
                    'Prevenir fraudes e abusos, como tentativas de teste de cartões, e manter a segurança: legítimo interesse (art. 7º, IX).',
                    'Atender solicitações e prestar suporte: execução do contrato e legítimo interesse.',
                ] },
                'Não vendemos dados pessoais. Também não os usamos para finalidades incompatíveis com as descritas aqui.',
            ],
        },
        {
            title: 'Pagamentos e dados de cartão',
            blocks: [
                'Os pagamentos são processados pelo Asaas, instituição de pagamento. O Asaas recebe os dados necessários para a cobrança e trata-os conforme a própria política.',
                'Os dados do cartão (número, validade e código de segurança) passam pelo nosso servidor apenas em trânsito, com criptografia (HTTPS), e seguem direto ao Asaas. ' +
                'Não os gravamos em banco de dados nem em registros de funcionamento.',
                'Para a cobrança automática, o Asaas devolve um token que representa o cartão. Esse token é usado só para criar a assinatura e é apagado dos nossos registros logo em seguida. ' +
                'Os dados do cartão ficam sob guarda do Asaas.',
            ],
        },
        {
            title: 'Com quem compartilhamos',
            blocks: [
                'Compartilhamos dados apenas com prestadores necessários à operação, que atuam como operadores ou parceiros de tratamento:',
                { list: [
                    'Asaas: processamento de pagamentos e cobranças;',
                    'Supabase: banco de dados, autenticação e execução do sistema (infraestrutura hospedada na região de São Paulo, Brasil);',
                    'Vercel: hospedagem da aplicação web;',
                    'serviços de integração que o próprio cliente ativa na Plataforma, como WhatsApp, agenda e assinatura eletrônica, nos limites dessa ativação.',
                ] },
                'Também podemos compartilhar dados quando exigido por lei, ordem judicial ou autoridade competente.',
            ],
        },
        {
            title: 'Transferência internacional',
            blocks: [
                'Alguns prestadores podem tratar dados em outros países. Nesses casos, adotamos as garantias previstas na LGPD (art. 33), ' +
                'como cláusulas contratuais e o uso de fornecedores que mantêm padrões adequados de proteção.',
            ],
        },
        {
            title: 'Por quanto tempo guardamos',
            blocks: [
                'Guardamos os dados enquanto a contratação estiver ativa e, depois, pelo prazo necessário para cumprir obrigações legais, ' +
                'fiscais e contábeis e para exercer ou defender direitos, inclusive o registro de aceite. Encerrado o prazo, os dados são excluídos ou anonimizados.',
                'Dados inseridos pelo cliente na Plataforma são tratados enquanto a conta existir e conforme as instruções do cliente.',
            ],
        },
        {
            title: 'Como protegemos',
            blocks: [
                'Adotamos medidas técnicas e administrativas para proteger os dados, como conexão criptografada (HTTPS), controle de acesso por login e por perfil, ' +
                'separação dos dados entre empresas e restrição de acesso interno ao necessário.',
                'Nenhum sistema é totalmente imune a incidentes. Em caso de incidente de segurança que possa causar risco relevante, comunicaremos os titulares e a autoridade competente nos termos da LGPD.',
            ],
        },
        {
            title: 'Seus direitos',
            blocks: [
                'Você pode solicitar, a qualquer momento e de forma gratuita:',
                { list: [
                    'confirmação de que tratamos seus dados e acesso a eles;',
                    'correção de dados incompletos, inexatos ou desatualizados;',
                    'anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em desconformidade;',
                    'portabilidade, quando aplicável;',
                    'informação sobre com quem compartilhamos os dados;',
                    'revogação do consentimento, quando o tratamento se basear nele, e revisão de decisões automatizadas.',
                ] },
                `Para exercer esses direitos, fale com a gente ${contactLine}. Se os dados forem de um cliente seu inseridos na Plataforma, o pedido deve ser feito ao ` +
                'controlador desses dados, isto é, à empresa que usa o sistema. Você também pode reclamar à Autoridade Nacional de Proteção de Dados (ANPD).',
            ],
        },
        {
            title: 'Armazenamento no navegador',
            blocks: [
                'Na página de contratação não usamos cookies de publicidade nem de rastreamento. Utilizamos apenas o armazenamento do navegador estritamente necessário ao funcionamento e à manutenção da sessão de acesso.',
            ],
        },
        {
            title: 'Alterações desta Política',
            blocks: [
                'Podemos atualizar esta Política para refletir mudanças no serviço ou na lei. A versão em vigor é a publicada nesta página, com a data de atualização indicada no topo. ' +
                'Mudanças relevantes serão comunicadas por e-mail ou pela Plataforma.',
            ],
        },
        {
            title: 'Contato e encarregado de dados',
            blocks: [
                `Dúvidas, solicitações e contato com o encarregado pelo tratamento de dados pessoais: ${contactLine}.`,
            ],
        },
    ],
};
